// src/pages/SuperAdmin/components/EditContractModal.tsx - Paramétrer le contrat annuel

import React, { useEffect, useState } from 'react';
import { Modal, Form, InputNumber, Input, DatePicker, message } from 'antd';
import type { Business } from '../../../types/business';
import type { BusinessContract } from '../../../types/businessContract';
import { useCreateBusinessContract, useUpdateBusinessContract } from '../../../hooks/useBusinessContracts';
import dayjs from 'dayjs';

interface EditContractModalProps {
  open: boolean;
  business: Business;
  contract: BusinessContract | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditContractModal: React.FC<EditContractModalProps> = ({
  open,
  business,
  contract,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const createMutation = useCreateBusinessContract();
  const updateMutation = useUpdateBusinessContract();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!contract;

  useEffect(() => {
    if (open) {
      if (contract) {
        form.setFieldsValue({
          startDate: dayjs(contract.startDate),
          endDate: dayjs(contract.endDate),
          monthlyAmount: contract.monthlyAmount,
          description: contract.description ?? '',
        });
      } else {
        form.resetFields();
        const start = dayjs();
        form.setFieldsValue({
          startDate: start,
          endDate: start.add(12, 'month'),
          monthlyAmount: undefined,
          description: '',
        });
      }
    }
  }, [open, contract, form]);

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    if (date && !isEditing) {
      form.setFieldValue('endDate', date.add(12, 'month'));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);
      if (isEditing) {
        await updateMutation.mutateAsync({
          contractId: contract.id,
          businessId: business.id,
          startDate: values.startDate.toDate(),
          endDate: values.endDate?.toDate(),
          monthlyAmount: values.monthlyAmount,
          description: values.description?.trim() || undefined,
        });
        message.success('Contrat mis à jour avec succès');
      } else {
        await createMutation.mutateAsync({
          businessId: business.id,
          startDate: values.startDate.toDate(),
          endDate: values.endDate?.toDate(),
          monthlyAmount: values.monthlyAmount,
          currency: 'FCFA',
          description: values.description?.trim() || undefined,
        });
        message.success('Contrat créé avec succès');
      }
      form.resetFields();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error('Erreur lors de l\'enregistrement du contrat');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isEditing ? 'Modifier le contrat' : 'Créer le contrat'}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={isSubmitting}
      okText={isEditing ? 'Enregistrer' : 'Créer'}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="startDate"
          label="Date de début du contrat"
          rules={[{ required: true, message: 'Choisissez la date de début' }]}
        >
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" onChange={handleStartDateChange} />
        </Form.Item>
        <Form.Item
          name="endDate"
          label="Date de fin du contrat"
          rules={[{ required: true, message: 'Choisissez la date de fin' }]}
        >
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item
          name="monthlyAmount"
          label="Mensualité (FCFA)"
          rules={[{ required: true, message: 'Saisissez la mensualité' }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            step={1000}
            formatter={(v) => String(v ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            addonAfter="FCFA"
          />
        </Form.Item>
        <Form.Item name="description" label="Description du contrat">
          <Input.TextArea rows={3} placeholder="Objet ou description du contrat (optionnel)" maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditContractModal;
